<template>
  <div class="tw:flex tw:flex-1 tw:flex-col tw:overflow-hidden">
    <div
      v-if="isMobile"
      class="tw:flex-1 tw:overflow-auto"
      data-testid="list"
    >
      <div
        v-for="source in localSelectedSources"
        :key="`${source.type}_${source.id}`"
        class="tw:flex tw:items-center tw:p-2"
        data-testid="list-item"
      >
        <div class="tw:flex-1">
          <div class="tw:flex">
            <div class="tw:flex-1">
              {{ source.name }}
            </div>
          </div>
          <div
            class="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-denim-700"
          >
            {{ source.location }}

            <div class="tw:text-xs">
              {{ getMediumColumnLabel(source) }}
            </div>
          </div>
        </div>

        <div>
          <BaseButton
            circle
            flat
            icon="close"
            new-design
            new-icon
            size="sm"
            @click="remove(source)"
          />
        </div>
      </div>
    </div>
    <div
      v-else
      class="c-table-container"
    >
      <table
        class="c-table"
        data-testid="table"
      >
        <thead>
          <tr>
            <th>
              <TableSortOrder
                v-model="sortOrder"
                :label="getLocaleText('global.name')"
                sort-by="name"
              />
            </th>
            <th>
              <template v-if="isCisionOne">
                <TableSortOrder
                  v-model="sortOrder"
                  :label="getLocaleText('source_selection.sector_heading')"
                  sort-by="sector"
                />
              </template>
              <template v-else>
                <TableSortOrder
                  v-model="sortOrder"
                  :label="getLocaleText('source_selection.category_heading')"
                  sort-by="category"
                />
              </template>
            </th>
            <th>
              <TableSortOrder
                v-model="sortOrder"
                :label="getLocaleText('source_selection.medium_heading')"
                sort-by="medium"
              />
            </th>
            <th>
              <TableSortOrder
                v-model="sortOrder"
                :label="getLocaleText('source_selection.source_group_heading')"
                sort-by="source_group_name"
              />
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="source in localSelectedSources"
            :key="`${source.type}_${source.id}`"
            class="tw:hover:bg-denim-100"
            @mouseenter="onMouseEnter(source)"
            @mouseleave="onMouseLeave"
          >
            <td>
              <div class="tw:max-w-[136px]">
                <EllipseContent>
                  {{ source.name }}
                </EllipseContent>
              </div>
            </td>
            <template v-if="!isMobile">
              <td>
                <div v-if="source.sector">
                  <div class="tw:max-w-[136px]">
                    <div
                      class="tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap"
                    >
                      {{ source.sector }}
                    </div>
                  </div>

                  <StreemTooltip
                    v-if="source.topics && source.topics.length > 1"
                  >
                    <div class="tw:flex tw:flex-col tw:gap-2 tw:p-2 tw:text-sm">
                      <div class="tw:font-bold">
                        {{ getLocaleText("source_selection.sector_heading") }}
                      </div>
                      <div>
                        <span
                          v-for="(topic, topicIndex) in source.topics"
                          :key="topic.id"
                        >
                          <template v-if="topicIndex">,</template>
                          {{ topic.name }}
                        </span>
                      </div>
                    </div>
                  </StreemTooltip>
                </div>
                <div v-else-if="!isCisionOne && source.category">
                  {{ source.category }}
                </div>
                <div v-else>-</div>
              </td>
              <td>
                {{ getMediumColumnLabel(source) }}
              </td>
              <td>
                <div class="tw:flex tw:items-center">
                  <div class="tw:flex-1">
                    <div
                      v-if="source.source_group_name"
                      class="tw:max-w-[100px]"
                    >
                      <EllipseContent>
                        {{ source.source_group_name }}
                      </EllipseContent>
                    </div>
                    <template v-else> - </template>
                  </div>
                  <div class="tw:relative tw:px-2">
                    <BaseButton
                      v-if="showRemoveButton(source)"
                      circle
                      class="tw:absolute tw:right-0 tw:top-0 tw:-mr-1 tw:-mt-3"
                      flat
                      icon="close"
                      new-design
                      new-icon
                      size="sm"
                      @click="remove(source)"
                    />
                  </div>
                </div>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>
    <BaseSeparator />
    <div class="tw:flex tw:justify-end tw:px-2 tw:py-1">
      <Pagination
        :page-size-options="[20, 50, 100]"
        :pagination="localPagination"
        @pagination-changed="onPaginationChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { chunk, orderBy } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, inject, ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, BaseSeparator } from "shared/components/base";
import EllipseContent from "shared/components/base/EllipseContent.vue";
import StreemTooltip from "shared/components/base/StreemTooltip.vue";
import { type PaginationData, Pagination } from "shared/components/Pagination";
import TableSortOrder from "shared/components/table/TableSortOrder.vue";
import type { MediumField } from "shared/helpers/media";
import { getSourceByField } from "shared/helpers/media";
import type { Source } from "shared/helpers/sourceFilters";
import { constantize } from "shared/helpers/string";
import useGlobalStore from "shared/stores/global";
import type { Nullable } from "shared/types/utility";

export interface SourceSelectionProps {
  query?: string;
  readOnly?: boolean;
}

const selectedSources = defineModel<Source[]>("selectedSources", {
  default: () => [],
});

const props = withDefaults(defineProps<SourceSelectionProps>(), {
  query: "",
});

const isMobile = inject("isMobile");

const { isCisionOne } = storeToRefs(useGlobalStore());

const pagination = ref<PaginationData>({
  page: 1,
  pageSize: 50,
  pageCount: 0,
});

interface SortOrder {
  sortBy: keyof Pick<
    Source,
    "name" | "category" | "medium" | "sector" | "source_group_name"
  >;
  sortDirection: "asc" | "desc";
}

const sortOrder = ref<SortOrder>({
  sortBy: "name",
  sortDirection: "asc",
});

const activeSource = ref<Nullable<Source>>(null);

const mappedSelectedSources = computed<Source[]>(() =>
  selectedSources.value.map((source) => {
    const sector =
      isCisionOne.value && source.topics && source.topics.length
        ? source.topics[0].name
        : "";

    const sourceGroupName =
      source.type !== "source_group_medium_location" && source.source_group
        ? source.source_group.name
        : "";

    return {
      ...source,
      sector,
      source_group_name: sourceGroupName,
    };
  })
);

const filteredSelectedSources = computed<Source[]>(() => {
  const sources = props.query
    ? mappedSelectedSources.value.filter((source) =>
        source.name.toLowerCase().includes(props.query.toLowerCase())
      )
    : mappedSelectedSources.value;

  return orderBy(
    sources,
    (source: Source) =>
      source[sortOrder.value.sortBy]
        ? source[sortOrder.value.sortBy]!.toLowerCase()
        : "",
    sortOrder.value.sortDirection
  );
});

const chunkSelectedSources = computed(() =>
  chunk(filteredSelectedSources.value, pagination.value.pageSize)
);

const localSelectedSources = computed(
  () => chunkSelectedSources.value[pagination.value.page - 1]
);

const localPagination = computed<PaginationData>(() => ({
  ...pagination.value,
  pageCount: chunkSelectedSources.value.length,
}));

function onMouseEnter(source: Source): void {
  activeSource.value = source;
}

function onMouseLeave(): void {
  activeSource.value = null;
}

function findSelectedSource(selectedSource: Source): Source | undefined {
  return selectedSources.value.find(
    (source) =>
      source.id === selectedSource.id && source.type === selectedSource.type
  );
}

function remove(selectedSource: Source): void {
  const foundSource = findSelectedSource(selectedSource);

  if (foundSource) {
    selectedSources.value = selectedSources.value.filter(
      (source) => source !== foundSource
    );
  }
}

function showRemoveButton(selectedSource: Source): boolean {
  if (props.readOnly || !activeSource.value) return false;

  const foundSource = findSelectedSource(selectedSource);
  if (!foundSource) return false;

  return (
    activeSource.value?.id === foundSource.id &&
    activeSource.value?.type === foundSource.type
  );
}

function onPaginationChange({ page, pageSize }: PaginationData) {
  if (
    pagination.value.page === page &&
    pagination.value.pageSize === pageSize
  ) {
    return;
  }

  pagination.value.page = page;
  pagination.value.pageSize = pageSize;
}

function getMediumLabel(medium: MediumField) {
  return getSourceByField(medium).label;
}

function getMediumColumnLabel(source: Source) {
  const type = constantize(source.target_type || source.type);

  if (type === "SourceGroupMediumLocation") {
    return getLocaleText("source_selection.source_group_heading");
  }

  return getMediumLabel(source.medium);
}
</script>

<style lang="scss" scoped>
.c-table-container {
  border: none;
}
</style>
